import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useCargando } from './cargandoContext';
import type { IUsuarioAutenticado } from '../../modelos/Autenticacion/entidades/IUsuarioAutenticado';
import { setUnauthorizedHandler } from '../../utils/api';
import { useConfirmacion } from './confirmacionContext';
import { EnumTipoNotificacion } from '../interfaces';
import { useNavigate } from 'react-router-dom';

interface TipoContextoAutenticacion {
  usuario: IUsuarioAutenticado | null;
  estaAutenticado: boolean;
  iniciarSesion: (usuarioAutenticado: IUsuarioAutenticado) => void;
  cerrarSesion: () => void;
  verificandoSesion: boolean;
}

const ContextoAutenticacion = createContext<TipoContextoAutenticacion | undefined>(undefined);

interface PropsProveedorAutenticacion {
  children: ReactNode;
}

export const ProveedorAutenticacion: React.FC<PropsProveedorAutenticacion> = ({ children }) => {
  const [usuario, setUsuario] = useState<IUsuarioAutenticado | null>(null);
  const [verificandoSesion, setVerificandoSesion] = useState(true);
  const { manejarCargando } = useCargando();
  const { confirmar } = useConfirmacion();
  const navigate = useNavigate();

  // Recuperar sesión desde localStorage al montar
  useEffect(() => {
    const verificarSesionGuardada = () => {
      manejarCargando(true, "Verificando sesión..."); // 👈 Mostrar loading global

      const usuarioGuardado = localStorage.getItem('usuario');
      const expirationGuardada = localStorage.getItem('expiration');

      if (usuarioGuardado && expirationGuardada) {
        const expirationDate = new Date(expirationGuardada);
        const ahora = new Date();

        if (expirationDate > ahora) {
          setUsuario(JSON.parse(usuarioGuardado));
        } else {
          localStorage.removeItem('usuario');
          localStorage.removeItem('authToken');
          localStorage.removeItem('expiration');
        }
      }

      setVerificandoSesion(false);
      manejarCargando(false); // 👈 Ocultar loading global
    };

    verificarSesionGuardada();
  }, [manejarCargando]);

  useEffect(() => {
    if (usuario) {
      // La cadena que viene del backend
      // const expirationStr = usuario.Expiration; // "2025-09-19T14:14:07.288Z"

      // Parseo "manual" ignorando la Z (UTC)
      // const expirationLocal = new Date(expirationStr.replace("Z", "")).getTime();

      // const ahora = Date.now();
      // const tiempoRestante = expirationLocal - ahora;

      // if (tiempoRestante > 0) {
      // } else {
      //   cerrarSesion();
      // }
    }
  }, [usuario]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      manejoSesionCaducada();
    });
  }, []);


  const iniciarSesion = (usuarioAutenticado: IUsuarioAutenticado) => {
    setUsuario(usuarioAutenticado);
    localStorage.setItem('usuario', JSON.stringify(usuarioAutenticado));
    localStorage.setItem('authToken', usuarioAutenticado.Token);
    localStorage.setItem('expiration', usuarioAutenticado.Expiration);
  };

  const cerrarSesion = () => {
    manejarCargando(true, "Cerrando sesión...");
    setUsuario(null);
    localStorage.removeItem('usuario');
    localStorage.removeItem('authToken');
    localStorage.removeItem('expiration');

    setTimeout(() => {
      manejarCargando(false);
      navigate('/ingresar'); // 👈 redirige al login
    }, 500);
  };


  const estaAutenticado = !!usuario;

  const manejoSesionCaducada = async () => {
    await confirmar({
      titulo: "Sesión caducada",
      mensaje: "Tu sesión ha caducado. Debes iniciar sesión de nuevo.",
      textoAceptar: "Aceptar",
      textoCancelar: '',
      tipo: EnumTipoNotificacion.Warning,
      opcionCerrarPopup: false
    });

    cerrarSesion();
  };



  const valor: TipoContextoAutenticacion = {
    usuario,
    estaAutenticado,
    iniciarSesion,
    cerrarSesion,
    verificandoSesion,
  };

  return (
    <ContextoAutenticacion.Provider value={valor}>
      {children}
    </ContextoAutenticacion.Provider>
  );
};

export const useAutenticacion = (): TipoContextoAutenticacion => {
  const contexto = useContext(ContextoAutenticacion);
  if (contexto === undefined) {
    throw new Error('useAutenticacion debe ser usado dentro de un ProveedorAutenticacion');
  }
  return contexto;
};
